import { ky, useSWRMutation } from './client'
import { CustomReportBlockTypeEnum } from '../models'
import useSWR, { useSWRConfig } from 'swr'
import { toast } from 'react-toastify'

export const ReportStatusIndicatorEnum = {
  UNSAVED: 'UNSAVED',
  SAVING: 'SAVING',
  SAVED: 'SAVED',
  ERROR: 'ERROR',
  REPORT_NAME_DUPLICATED: 'REPORT_NAME_DUPLICATED',
  VALIDATION_ERROR: 'VALIDATION_ERROR'
}

/**
 * Saves a list of plans.
 * @param {{
 *  sectionId: string,
 *  to?: number | null,
 *  from?: number | null,
 * }} query
 *
 * TODO: Type response
 * @returns { Promise<object | undefined> }
 */
const getSectionComments = async ({ sectionId, to, from }) => {
  try {
    const comments = await ky.get(`reports/comments/${sectionId}`, {
      searchParams: {
        ...(to ? { to } : {}),
        ...(from ? { from } : {}),
        version: '2.0'
      }
    })
    const commentsData = await comments.json()

    return {
      criteriaComments: commentsData.criteria.comments.map((c) => ({
        text: c.comment,
        planInternalId: c.assignment.plan.internalId,
        commenterName: c.assignment.user.name,
        commentDate: c.assignment.completedAt
      }))
    }
  } catch (e) {
    console.error(e)
    // TODO: Return better than `undefined` on error
  }
}

/**
 * get comment settings for form
 */
export const getCommentSettings = async () => {
  try {
    const settings = await ky.get(`reports/form/comments`)
    const settingsData = await settings.json()
    return settingsData
  } catch (e) {
    console.error(e)
  }
}

const getByGrade = async ({ filters, groupBy }) => {
  try {
    const response = await ky
      .post(`audit/reports/byGrade`, {
        json: { filters, sectionGroup: groupBy }
      })
      .catch(console.error)
    return await response.json()
  } catch (e) {
    console.error(e)
    return { data: [] }
  }
}

const getGraphFilters = async () => {
  try {
    const response = await ky.get(`audit/reports/filters`).catch(console.error)
    const refs = await response.json()

    // Filters
    const filters = {
      completedDate: { title: 'Audit Completed date', style: 'date' },
      assignedDate: { title: 'Assigned date', style: 'date' },
      uploadedDate: { title: 'Uploaded date', style: 'date' }
    }

    Object.entries(refs.filters).forEach(([key, list]) => {
      /* Makes the title pretty. */
      const prettify = (string) => {
        const split = string.replace(/([A-Z])/g, ' $1')
        return split[0].toUpperCase() + split.slice(1)
      }

      // Maps list fields.
      filters[key] = {
        title: prettify(key),
        style: 'list',
        list: list.map((obj) => {
          // Sqaushes object to value
          // [{ key: value }] => [value]
          const k = Object.keys(obj)[0]

          return { label: obj[k], value: obj[k] }
        })
      }
    })

    // Groupings.
    const groups = refs.groupBy.section.map((s) => ({
      label: s.description ? `${s.name} - ${s.description}` : s.name,
      value: s.id
    }))

    return {
      filters,
      groupBy: groups
    }
  } catch (e) {
    console.error(e)
    return { filters: [], groupBy: [] }
  }
}

/**
 * @param {any[]} items
 * @param {{ labels: Record<string, string> }} context
 */
export const getReportBuilderPDF = async (items, context) => {
  try {
    const request = await ky
      .post('reports/export/custom-report', {
        json: { items, context },
        responseType: 'arraybuffer',
        headers: {
          Accept: 'application/pdf',
          'Content-Disposition': 'attachment; filename=name.Pdf'
        }
      })
      .blob()
    window.open(URL.createObjectURL(request))
  } catch (error) {
    console.error(error)
    return {
      count: 0,
      rows: []
    }
  }
}

/**
 * we can use this function to make all the required requests for report builder. For both blocks and form settings.
 * A new case will be added for each block and its setting
 * @param {*} option
 * @param {*} opts
 * @returns report builder data from api
 */

export const selectReportBuilderOptions = async (option, opts) => {
  switch (option) {
    case 'COMMENTS':
      return await getSectionComments({
        sectionId: opts?.section.id,
        to: opts?.to,
        from: opts?.from
      })
    case 'SETTING_COMMENTS':
      // TODO: const data = await getCommentSettings();
      return []

    case 'CUSTOM_CHART':
      return await getByGrade(opts)
    case 'SETTING_CUSTOM_CHART':
      return await getGraphFilters()

    default:
      throw new Error('no option selected ')
  }
}

/**
 *
 * @param {Array} filters
 * @returns {Object}
 */
export const getOutcomesMonitoringData = async (filters) => {
  try {
    const response = await ky.post('reports/outcomes-monitoring', { json: { filters } }).catch(console.error)
    const data = await response.json()
    return data
  } catch (e) {
    console.error(e)
    return { data: [] }
  }
}

/**
 * Fetches reference data for Outcomes monitoring.
 */
export const getOutcomesMonitoringReferences = async () => {
  try {
    const response = await ky.get('reports/outcomes-monitoring/references', {}).catch(console.error)
    const refs = await response.json()

    const filters = {
      reviewDate: { title: 'Review Date', style: 'date' },
      completedAt: { title: 'Audit Completed Date', style: 'date' }
    }

    Object.entries(refs).forEach(([key, list]) => {
      /* Makes the title pretty. */
      const prettify = (string) => {
        const split = string.replace(/([A-Z])/g, ' $1')
        return split[0].toUpperCase() + split.slice(1)
      }

      // Maps list fields.
      filters[key] = {
        title: prettify(key),
        style: 'list',
        list: list.map((value) => {
          return { label: value, value }
        })
      }
    })

    return { filters }
  } catch (e) {
    console.error(e)
    return { filters: [] }
  }
}

export const saveNewReport = async (body, setReportStatusIndicator) => {
  try {
    const response = await ky.post('reports/create/custom-report', {
      json: body
    })
    const resData = await response.json()

    if (response.status === 400) {
      return setReportStatusIndicator(ReportStatusIndicatorEnum.REPORT_NAME_DUPLICATED)
    }

    return resData?.reports
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const getReportList = async () => {
  try {
    const response = await ky.get('reports/list/custom-reports')
    const resData = await response.json()

    return resData?.reportsList
  } catch (e) {
    console.error(e)
    throw e
  }
}

const fetchCustomReportBlockContent = async (block) => {
  const { type, config } = block

  if (type === CustomReportBlockTypeEnum.COMMENTS) {
    const content = await getSectionComments({
      sectionId: config.section.id,
      to: config.to,
      from: config.from
    })

    return { ...block, content }
  }

  if (type === CustomReportBlockTypeEnum.CUSTOM_CHART) {
    const content = await getByGrade({
      filters: config.filters,
      groupBy: config.groupBy
    })

    return { ...block, content }
  }

  return block
}

export const useCustomReportList = () => {
  const key = 'reports/list/custom-reports'

  const fetcher = async (key) => {
    const response = await ky.get(key)
    const { reportsList } = await response.json()

    return reportsList
  }

  return useSWR(key, fetcher, {
    revalidateOnMount: true
  })
}

export const useCustomReport = ({ id }) => {
  const key = `reports/custom/${id}`

  const fetcher = async (key) => {
    const response = await ky.get(key)
    const report = await response.json()

    // Fetch content
    const content = report.content
    const populatedContent = await Promise.all(
      content.map(async (block) => {
        return await fetchCustomReportBlockContent(block)
      })
    )

    return { ...report, content: populatedContent }
  }

  return useSWR(key, fetcher, {
    revalidateOnMount: true,
    refreshInterval: 30 * 1000
  })
}

export const updateReports = async (updatedReportData, setReportStatusIndicator) => {
  try {
    const response = await ky.put('reports/update/custom-reports', {
      json: { ...updatedReportData }
    })
    if (response.status === 400) {
      return setReportStatusIndicator(ReportStatusIndicatorEnum.REPORT_NAME_DUPLICATED)
    }
    const resData = await response.json()
    return resData?.updateReports
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const deleteReports = async (reportId) => {
  try {
    await ky.delete('reports/delete/custom-reports', {
      json: { id: reportId }
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const useDeleteReport = (opts) => {
  const { mutate, cache } = useSWRConfig()

  const fetcher = async (key, { arg: id }) => {
    await ky.delete(key, { json: { id } })
    return id
  }

  const { trigger: rawTrigger, isMutating, error } = useSWRMutation('reports/delete/custom-reports', fetcher, opts)

  // Wrapped trigger to handle optimistic updates
  const trigger = async (id, opts) => {
    const listKey = 'reports/list/custom-reports'

    const { data: previousList = [] } = cache.get(listKey) || {}

    await mutate(listKey, (current = []) => current.filter((r) => r.id !== id), {
      populateCache: true,
      revalidate: false
    })

    try {
      const deletedId = await rawTrigger(id, opts)

      await mutate(listKey)
      return deletedId
    } catch (err) {
      toast.error('Report deletion failed')
      await mutate(listKey, previousList, { revalidate: false })
    }
  }

  return { trigger, isMutating, error }
}
